/**
 * Main landing page hero with create board call to action
 *
 * Copyright (c) 2022 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import nanoid from 'nanoid'
import isEmpty from 'lodash/isEmpty'
import queryString from 'query-string'
import { Form, Button, Tooltip } from 'reactstrap'
import { GoogleMapsApiWrapper } from 'client/shared/js/components/GoogleMaps'
import GooglePlacesAutoComplete from 'client/shared/js/components/GooglePlacesAutoComplete'
import useAutoClearMessage from 'client/shared/js/hooks/useAutoClearMessage'
import { newBoardPath } from 'client/app/js/scenes/routes/board'
// import { trackEvent } from 'client/shared/js/utils/tracking'
import styles from './CreateBoardForm.module.scss'

const CreateBoardForm = ({ className, maps, btnText, isNarrowWidth }) => {
  const [location, setLocation] = useState(null)
  const [errMsg, setErrMsg] = useState('')
  const id = useMemo(() => `create-board-form-${nanoid()}`, [])

  useAutoClearMessage(errMsg, setErrMsg, 3000)

  const createBoard = useCallback((currLocation) => {
    if (currLocation && !isEmpty(currLocation)) {
      const url = queryString.stringifyUrl({
        url: newBoardPath,
        query: {
          locationObject: JSON.stringify(currLocation),
          autoSubmit: true
        }
      })
      window.location.assign(url)
    } else {
      setErrMsg('Please select a valid location')
    }
  }, [])

  const handleSuggestionSelected = useCallback((place) => {
    // If no btnText is provided, we want to trigger a submit on suggestion selected
    if (btnText) {
      setLocation(place?.location)
    } else {
      createBoard(place?.location)
    }
  }, [createBoard, btnText])

  const handleSaveSubmit = useCallback((evt) => {
    if (evt && evt.preventDefault) {
      evt.preventDefault()
    }

    createBoard(location)
    // trackEvent()
  }, [createBoard, location])

  return maps && (
    <Form
      className={cx(className, styles['create-board-form'])}
      onSubmit={handleSaveSubmit}>
      <GooglePlacesAutoComplete
        id={id}
        placeholder={`Where are you going?`}
        maps={maps}
        type='city'
        onSuggestionSelected={handleSuggestionSelected}
        highlightFirstSuggestion
        disableAttribution
        disableAutoFocus />
      {btnText && (
        <Button
          color='primary'
          type='submit'>
          {btnText}
        </Button>
      )}
      <Tooltip
        className={styles['invalid-location-msg']}
        isOpen={!!errMsg}
        target={id}
        placement={isNarrowWidth ? 'top' : 'right'}>
        {errMsg}
      </Tooltip>
    </Form>
  )
}

CreateBoardForm.propTypes = {
  className: PropTypes.string,
  maps: PropTypes.object,
  btnText: PropTypes.string,
  isNarrowWidth: PropTypes.bool
}

export default GoogleMapsApiWrapper(CreateBoardForm)
